import React, { useEffect, useState } from 'react';
import { demoSignupsAPI } from '../services/api';

const TOKEN_STORAGE_KEY = 'receipt-tracker-demo-signups-token';

function DemoSignupsPage() {
  const [token, setToken] = useState('');
  const [submittedToken, setSubmittedToken] = useState('');
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [signups, setSignups] = useState([]);

  useEffect(() => {
    const savedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
    if (savedToken) {
      setToken(savedToken);
      setSubmittedToken(savedToken);
    }
  }, []);

  useEffect(() => {
    if (!submittedToken) {
      return;
    }

    const loadSignups = async () => {
      try {
        setLoading(true);
        const data = await demoSignupsAPI.listAdmin(submittedToken);
        setSummary(data.summary || null);
        setSignups(data.signups || []);
        setStatus({ type: 'success', message: '' });
      } catch (error) {
        setSummary(null);
        setSignups([]);
        setStatus({
          type: 'error',
          message: error?.response?.status === 401
            ? 'That token was rejected.'
            : error?.response?.data?.error || 'Could not load demo signups.',
        });
      } finally {
        setLoading(false);
      }
    };

    loadSignups();
  }, [submittedToken]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalizedToken = String(token || '').trim();

    if (!normalizedToken) {
      setStatus({ type: 'error', message: 'Enter your admin token to continue.' });
      return;
    }

    window.localStorage.setItem(TOKEN_STORAGE_KEY, normalizedToken);
    setSubmittedToken(normalizedToken);
  };

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-8 text-stone-100 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-[32px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/80">Demo Tracker</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-50">Demo signups</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-400">
            Use your admin token to view captured emails and export them as CSV.
          </p>

          <form className="mt-6 flex flex-col gap-3 sm:flex-row" onSubmit={handleSubmit}>
            <input
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="Enter admin token"
              className="w-full rounded-2xl border border-white/10 bg-stone-900 px-4 py-3 text-sm text-stone-50 outline-none transition placeholder:text-stone-500 focus:border-amber-300/60 focus:ring-2 focus:ring-amber-200/20"
            />
            <button
              type="submit"
              className="rounded-2xl bg-amber-300 px-5 py-3 text-sm font-semibold text-stone-950 transition hover:bg-amber-200"
            >
              Load signups
            </button>
            {submittedToken ? (
              <a
                href={demoSignupsAPI.getExportUrl(submittedToken)}
                className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-stone-100 transition hover:bg-white/10"
              >
                Export CSV
              </a>
            ) : null}
          </form>

          {status.message ? (
            <div
              className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                status.type === 'error'
                  ? 'border border-rose-400/30 bg-rose-400/10 text-rose-100'
                  : 'border border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
              }`}
            >
              {status.message}
            </div>
          ) : null}
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-stone-400">Total signups</p>
            <p className="mt-2 text-3xl font-semibold text-stone-50">{summary?.total ?? '-'}</p>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-stone-400">Today</p>
            <p className="mt-2 text-3xl font-semibold text-stone-50">{summary?.today ?? '-'}</p>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-stone-400">Latest signup</p>
            <p className="mt-2 text-sm font-medium text-stone-50">
              {summary?.latestSignupAt ? new Date(summary.latestSignupAt).toLocaleString() : '-'}
            </p>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-[32px] border border-white/10 bg-white/5">
          <div className="border-b border-white/10 px-5 py-4">
            <h2 className="text-lg font-semibold text-stone-50">Captured emails</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 text-left">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.22em] text-stone-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Captured</th>
                  <th className="px-5 py-3 font-medium">Referrer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm text-stone-200">
                {loading ? (
                  <tr>
                    <td className="px-5 py-6 text-stone-400" colSpan="4">Loading signups...</td>
                  </tr>
                ) : signups.length ? (
                  signups.map((signup) => (
                    <tr key={signup.id}>
                      <td className="px-5 py-4 font-medium text-stone-50">{signup.email}</td>
                      <td className="px-5 py-4">{signup.source}</td>
                      <td className="px-5 py-4 text-stone-400">{new Date(signup.createdAt).toLocaleString()}</td>
                      <td className="px-5 py-4 text-stone-400">{signup.referrer || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-5 py-6 text-stone-400" colSpan="4">No signups yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoSignupsPage;
